import { useEffect, useMemo } from 'react';
import useDefaultValue from './useDefaultValue';
import { CanvasTexture, MathUtils, Texture } from 'three';
import { decomposeColor } from '../../../utils/ColorUtils';

export type CanvasTextStyle = {
  bgColor?: number,
  bgAlpha?: number,
  textColor?: number,
  textAlign?: CanvasTextAlign,
  fontSize?: number,
  fontWeight?: number|string,
  font?: string,
  padding?: number
};

export default function useTextTexture(height: number, text = '', style?: CanvasTextStyle, resolution?: number, rectRadius = 20): Texture
{
  const _padding = useDefaultValue(style?.padding, 0.05);
  const _textColor = useDefaultValue(style?.textColor, 0xffffff);
  const _textAlign = useDefaultValue(style?.textAlign, 'center');
  const _font = useDefaultValue(style?.font, 'Nexa');
  const _fontWeight = useDefaultValue(style?.fontWeight, 900);
  const _resolution = useDefaultValue(resolution, 1200);
  const _fontSize = useDefaultValue(style?.fontSize, 15);
  const _bgColor = useMemo(() => decomposeColor(style?.bgColor === undefined? 0x000000 : style.bgColor), [style?.bgColor]);
  const _bgAlpha = useDefaultValue(style?.bgAlpha, style?.bgColor === undefined? 1 : 0);
  const canvas = useMemo(() => document.createElement('canvas'), []);
  const texture = useMemo(() => new CanvasTexture(canvas), [ canvas ]);

  const ctx = useMemo(() => canvas.getContext('2d'), [ canvas ]);

  useEffect(() =>
  {
    if (!ctx) return;

    const h = height * _resolution;
    const fontProp = _fontWeight + ' ' + Math.floor(_fontSize * h / 30) + 'px ' + _font;

    ctx.textBaseline = 'middle';
    ctx.font = fontProp;

    const width = ctx.measureText(text).width / _resolution + _padding;

    const w = width * _resolution;

    canvas.width = MathUtils.ceilPowerOfTwo(w);
    canvas.height = MathUtils.ceilPowerOfTwo(h);
    // canvas.width = Math.ceil(w);
    // canvas.height = Math.ceil(h);

    ctx.scale(canvas.width / w, canvas.height / h);
    ctx.clearRect(0, 0, w, h);

    if (_bgAlpha !== 0) {
      // premultiply alpha!
      const r = Math.floor(_bgColor.r * _bgAlpha);
      const g = Math.floor(_bgColor.g * _bgAlpha);
      const b = Math.floor(_bgColor.b * _bgAlpha);
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${_bgAlpha})`;
      roundRect(ctx, 0, 0, w, h, rectRadius);
    }

    ctx.textBaseline = 'middle';
    ctx.font = fontProp;
    ctx.textAlign = _textAlign;
    ctx.fillStyle = '#' + _textColor.toString(16);

    let x = 0;
    if (_textAlign === 'center')
      x = w * .5;
    else if (_textAlign === 'right')
      x = w;

    ctx.fillText(text, x, h * .54);

    texture.generateMipmaps = true;
    texture.needsUpdate = true;
    texture.image.nativeWidth = w;
    texture.image.nativeHeight = h;

  }, [ height, _resolution, _fontSize, _font, text, _padding, _textAlign, _textColor, rectRadius, ctx, canvas, _bgColor, _bgAlpha, _fontWeight, texture ]);

  return texture;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number, fill = true, stroke = false)
{
  if (typeof stroke === 'undefined') {
    stroke = true;
  }
  if (typeof radius === 'undefined') {
    radius = 5;
  }
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();

  if (fill) {
    ctx.fill();
  }

  if (stroke) {
    ctx.stroke();
  }
}
